import { useCallback, useEffect, useRef, useState } from "react";
import { MagnifyingGlass, Flask } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { LOOKUP_KIND_LABELS, LOOKUP_STATUS, d, dialerError, fmtDateTime, notifyDialerChanged } from "../../../lib/dialer";

const FIELD_LABELS = { name: "Nombre", address: "Dirección", city: "Ciudad", state: "Estado", zip: "ZIP" };

/**
 * Consulta manual de información externa (Melissa real si hay licencia; si no, simulada). SIEMPRE la activa una persona.
 * Un segundo clic se une a la solicitud ya reservada (lo garantiza el servidor). El resultado es
 * "Posible cliente, pendiente de confirmar": nunca toca la ficha; solo lo que una persona confirma.
 */
export default function BubbleLookupPanel({ call, canAct, notify, hasClient, onUseInNewClient }) {
  const [kind, setKind] = useState("telefono_inverso");
  const [items, setItems] = useState([]);
  const [denied, setDenied] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState({});
  const pending = useRef(false);

  const load = useCallback(async () => {
    try { const r = await d.get(`/calls/${call.id}/lookups`); setItems(r.data); setDenied(""); }
    catch (e) { setItems([]); setDenied(e?.response?.status === 403 ? "No tienes permiso para consultas externas en esta llamada." : dialerError(e)); }
  }, [call.id]);

  useEffect(() => { load(); }, [load]);
  // Mientras haya una consulta en curso se vuelve a leer (solo lectura: nunca reenvía nada).
  useEffect(() => {
    pending.current = items.some((i) => ["reservada", "enviando"].includes(i.status));
    if (!pending.current) return undefined;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [items, load]);

  const search = async () => {
    setBusy(true);
    try {
      const r = await d.post(`/calls/${call.id}/lookups`, { kind });
      notify.success(r.data.joined ? "Ya había una consulta reservada: te uniste a ella (no se envió otra)." : `Consulta reservada y enviada${r.data?.lookup?.simulated ? " (SIMULADA)" : ""}.`);
      await load();
    } catch (e) { notify.error(dialerError(e)); } finally { setBusy(false); }
  };

  const confirm = async (lk) => {
    const fields = Object.keys(picked[lk.id] || {}).filter((k) => picked[lk.id][k]);
    if (!fields.length) { notify.error("Marca al menos un dato para confirmar."); return; }
    setBusy(true);
    try {
      const r = await d.post(`/lookups/${lk.id}/confirm`, { fields });
      await load();
      const data = r.data.confirmed_data || {};
      if (hasClient) {
        if (window.confirm(`Pasar a la ficha del cliente: ${Object.entries(data).map(([k, v]) => `${FIELD_LABELS[k]}: ${v}`).join(", ")}?`)) {
          await api.put(`/clients/${call.client_id}`, data);
          notify.success("Datos confirmados pasados a la ficha.");
        }
      } else onUseInNewClient(data);
      notifyDialerChanged();
    } catch (e) { notify.error(dialerError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 text-sm" data-testid="bubble-lookup-panel">
      {items.length > 0 && (items[0].simulated
        ? <div className="text-[11px] flex items-center gap-1 text-amber-300"><Flask size={12} /> Proveedor SIMULADO (resultados ficticios). No se conecta ninguna API real.</div>
        : <div className="text-[11px] flex items-center gap-1 text-emerald-300"><MagnifyingGlass size={12} /> {items[0].provider || "Proveedor real"}: consulta real, usa créditos.</div>)}
      <div className="flex gap-2">
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs flex-1" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="lookup-kind">
          {Object.entries(LOOKUP_KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button size="sm" disabled={!canAct || busy || !!denied} onClick={search} data-testid="lookup-go"><MagnifyingGlass size={14} className="mr-1" /> Buscar información</Button>
      </div>
      {denied && <div className="text-xs text-muted-foreground" data-testid="lookup-denied">{denied}</div>}
      {items.map((lk) => {
        const st = LOOKUP_STATUS[lk.status] || { label: lk.status, cls: "" };
        return (
          <div key={lk.id} className="rounded-md border border-border p-2 space-y-1" data-testid={`lookup-${lk.kind}`}>
            <div className="flex items-center justify-between text-xs">
              <b>{LOOKUP_KIND_LABELS[lk.kind] || lk.kind}</b>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${st.cls}`} data-testid={`lookup-status-${lk.kind}`}>{st.label}</span>
            </div>
            <div className="text-[11px] text-muted-foreground" data-testid={`lookup-display-${lk.kind}`}>{lk.display}</div>
            <div className="text-[10px] text-muted-foreground">{lk.provider}{lk.queried_at ? ` · ${fmtDateTime(lk.queried_at)}` : ""} · {lk.participants?.length || 1} persona(s) en esta consulta</div>
            {lk.status === "con_resultado" && lk.candidate && (
              <div className="text-xs space-y-1">
                {Object.entries(FIELD_LABELS).filter(([k]) => lk.candidate[k]).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2">
                    <input type="checkbox" disabled={!!lk.confirmed_at} checked={lk.confirmed_at ? (lk.confirmed_fields || []).includes(k) : !!picked[lk.id]?.[k]}
                      onChange={(e) => setPicked((p) => ({ ...p, [lk.id]: { ...(p[lk.id] || {}), [k]: e.target.checked } }))} data-testid={`lookup-field-${k}`} />
                    <span className="text-muted-foreground">{label}:</span> {lk.candidate[k]}
                  </label>
                ))}
                {!lk.confirmed_at && <Button size="sm" variant="outline" disabled={!canAct || busy} onClick={() => confirm(lk)} data-testid="lookup-confirm">Confirmar los datos marcados</Button>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
